/** XeAgents.ai**/
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles } from "lucide-react";
import { StepCard, Field } from "./StepCard";
import { WebsiteExtractor } from "./WebsiteExtractor";
import { IcpPanel } from "./IcpPanel";
import type { DetectedProduct, ExtractionStage, IcpProfile } from "./types";

/**
 * Who you are, what you sell, and the website that can answer both.
 *
 * The URL sits at the top because it is the fastest path through the rest of the
 * page; the fields under it are what the read fills in, and what somebody
 * without a usable website types themselves. Both routes end in the same place,
 * which is why the inputs are always visible rather than revealed after a read.
 */
export function CompanyProductDetails({
  websiteUrl,
  onWebsiteUrlChange,
  extraction,
  analysisSteps,
  onChooseProduct,
  raw,
  onValueChange,
  onBlur,
  error,
  audienceError,
  icp,
  onIcpChange,
  onGenerate,
  canGenerate,
  generating,
  hasContent,
  generateHint,
  audienceLine,
  icpStale,
}: {
  websiteUrl: string;
  onWebsiteUrlChange: (value: string) => void;
  extraction: ExtractionStage;
  analysisSteps: readonly string[];
  onChooseProduct: (product: DetectedProduct) => void;
  raw: (key: string) => string;
  onValueChange: (key: string, value: string) => void;
  onBlur: (key: string) => void;
  error: (key: string) => string | undefined;
  /** Audience answers report themselves without waiting for a blur. */
  audienceError: (key: string) => string | undefined;
  icp: IcpProfile;
  onIcpChange: (patch: Partial<IcpProfile>) => void;
  onGenerate: () => void;
  canGenerate: boolean;
  generating: boolean;
  hasContent: boolean;
  generateHint: string;
  /** The spoken audience line the chosen industry produces. */
  audienceLine: string;
  icpStale: boolean;
}) {
  return (
    <StepCard
      step={2}
      title="Company & Product Details"
      description="Point us at your website and we'll fill these in — or type them yourself."
    >
      <WebsiteExtractor
        url={websiteUrl}
        onUrlChange={onWebsiteUrlChange}
        stage={extraction}
        steps={analysisSteps}
        onChooseProduct={onChooseProduct}
      />

      <div className="grid gap-5 border-t pt-5 sm:grid-cols-2">
        <Field
          label="Company Name"
          htmlFor="company-name"
          required
          hint="Spoken in the opening line of every call."
          error={error('company_name')}
        >
          {/* No placeholder. A greyed-out "Xenonlabs.ai" sits exactly where a
              real answer would and reads as one, which is how a page with
              nothing filled in looked finished. */}
          <Input
            id="company-name"
            value={raw('company_name')}
            onChange={(event) => onValueChange('company_name', event.target.value)}
            onBlur={() => onBlur('company_name')}
            aria-invalid={!!error('company_name')}
            data-testid="input-company-name"
          />
        </Field>

        <Field
          label="Product / Service"
          htmlFor="product-name"
          required
          hint="The one thing this template pitches."
          error={error('product_name')}
        >
          <Input
            id="product-name"
            value={raw('product_name')}
            onChange={(event) => onValueChange('product_name', event.target.value)}
            onBlur={() => onBlur('product_name')}
            aria-invalid={!!error('product_name')}
            data-testid="input-product-name"
          />
        </Field>
      </div>

      <IcpPanel
        icp={icp}
        onChange={onIcpChange}
        audienceLine={audienceLine}
        staleAfterEdit={icpStale}
        error={audienceError}
      />

      {/* One press for the whole of Step 2: it reads the website if there is one,
          fills the fields above from it, and writes all of Step 3. Two buttons
          here made people read the site and then wonder why Step 3 was empty.

          The label names what comes out. "Regenerate" on its own answered "again?"
          but never "of what?", on a button that silently rewrites all three fields
          of the next step - and it sits directly under a warning saying the
          audience changed, where "regenerate" could as easily have meant re-reading
          the website. */}
      <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center">
        <Button
          type="button"
          size="lg"
          onClick={onGenerate}
          disabled={!canGenerate || generating}
          className="shrink-0"
          data-testid="button-generate-step3"
        >
          {generating ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Writing your sales content...</>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              {hasContent ? "Regenerate Sales Content" : "Generate Sales Content"}
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground">
          {generateHint}
        </p>
      </div>

    </StepCard>
  );
}
